import { usePlatformModel } from "../assistant/external";
import { config, resolvedDataSource, type AppConfig } from "../config/env";
import type { Bootstrap, DataSource } from "./dataSource";
import { MockSource } from "./mockSource";
import { platformToken } from "../demo/auth/platformIdentity";
import { PlatformSource } from "./platformSource";

export interface StartedSource {
  source: DataSource;
  bootstrap: Bootstrap;
  /** Set when the platform was configured but unreachable and mock took over. */
  fallbackReason?: string;
}

/**
 * Chooses the adapter, runs the bootstrap once, and — when configured to —
 * falls back to the mock adapter if the platform cannot be reached at
 * start-up. The fallback is reported, never silent: the shell shows a
 * SAMPLE DATA banner naming the reason for as long as it is in effect.
 */
export async function startDataSource(c: AppConfig = config): Promise<StartedSource> {
  const kind = resolvedDataSource(c);
  if (kind === "platform") {
    if (!c.apiUrl) throw new Error("VITE_DATA_SOURCE=platform but VITE_SLT_API_URL is not set");
    const platform = new PlatformSource({
      baseUrl: c.apiUrl,
      apiKey: c.apiKey,
      devTenant: c.devTenant,
      devPrincipal: c.devPrincipal,
      /* A build with a provider acts as the person signed in, never as a key. */
      ...(c.identity ? { token: platformToken, apiKey: undefined } : {}),
    });
    try {
      const bootstrap = await platform.bootstrap();
      await registerModel(platform);
      return { source: platform, bootstrap };
    } catch (err) {
      /* Signed in as a real person, the sample is never a stand-in: it would
         show them an airport that is not theirs under their own name. */
      if (!c.mockFallback || c.identity) throw err;
      const reason = err instanceof Error ? err.message : String(err);
      const mock = new MockSource();
      return { source: mock, bootstrap: await mock.bootstrap(), fallbackReason: reason };
    }
  }
  const mock = new MockSource();
  usePlatformModel(null);
  return { source: mock, bootstrap: await mock.bootstrap() };
}

/**
 * Tell the assistant whether this deployment can compose wording for it.
 *
 * Asked once, at start-up, rather than before every question: the answer is
 * a deployment setting and does not change while the app is open, and a
 * question that has to discover it first pays a round trip before it can
 * begin. A deployment that holds no key registers nothing, which is how the
 * console knows to fall back to a key of its own instead of calling and
 * being refused.
 *
 * This never fails a start-up. The assistant is not what the platform is
 * for, and a console that will not open because a model could not be
 * enquired about has its priorities backwards.
 */
async function registerModel(platform: PlatformSource): Promise<void> {
  try {
    const m = await platform.skyModel();
    usePlatformModel(
      m.configured
        ? {
            model: m.model ?? "the deployment's model",
            compose: async (brief, material, signal) => {
              const out = await platform.composeWithModel(brief, material, signal);
              return { text: out.text, model: out.model };
            },
          }
        : null,
    );
  } catch {
    usePlatformModel(null);
  }
}
